import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../../api';

/**
 * 경영평가 > 편람 PDF 미리보기 + 페이지별 메모
 *
 * - PDF를 다운로드하지 않고 axios(blob)로 받아 브라우저 내장 뷰어(iframe)에 표시
 * - 좌측 지표 목록 클릭 시 해당 페이지로 점프 (#page= 프래그먼트)
 * - 하단 메모 패널: 현재 보고 있는 PDF 페이지에 메모를 남기고 서버에 저장
 */
export default function EvaluationManualViewer() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [manual, setManual] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [error, setError] = useState(null);
  const blobRef = useRef(null);

  // 메모: { [page_number]: [{ id, content, created_by_name, created_at }, ...] }
  const [memos, setMemos] = useState({});
  const [memoDraft, setMemoDraft] = useState('');
  const [memoSaving, setMemoSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meta, pdf] = await Promise.all([
          api.get(`evaluation/manuals/${id}/`),
          api.get(`evaluation/manuals/${id}/pdf/`, { responseType: 'blob' }),
        ]);
        if (cancelled) return;
        setManual(meta.data);
        const url = URL.createObjectURL(
          new Blob([pdf.data], { type: 'application/pdf' })
        );
        blobRef.current = url;
        setBlobUrl(url);
      } catch (e) {
        console.error(e);
        setError('PDF를 불러오지 못했습니다. 파일이 업로드되어 있는지 확인해주세요.');
      }
    })();
    return () => {
      cancelled = true;
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    };
  }, [id]);

  // 편람의 메모 전체 로드 → 페이지별 배열로 그룹핑
  const fetchMemos = useCallback(async () => {
    try {
      const res = await api.get('evaluation/memos/', { params: { manual: id } });
      const list = res.data.results ?? res.data;
      const map = {};
      list.forEach((m) => {
        if (!map[m.page_number]) map[m.page_number] = [];
        map[m.page_number].push(m);
      });
      setMemos(map);
    } catch (e) {
      console.error(e);
    }
  }, [id]);

  useEffect(() => { fetchMemos(); }, [fetchMemos]);

  // 페이지 이동 시 입력창은 항상 비움 (등록 방식이라 이전 내용 안 불러옴)
  useEffect(() => {
    setMemoDraft('');
  }, [page]);

  const handleAddMemo = async () => {
    if (!memoDraft.trim()) return;
    setMemoSaving(true);
    try {
      await api.post('evaluation/memos/', {
        manual: id,
        page_number: page,
        content: memoDraft,
      });
      setMemoDraft('');
      await fetchMemos();
    } catch (e) {
      console.error(e);
      alert('메모 등록에 실패했습니다.');
    } finally {
      setMemoSaving(false);
    }
  };

  const handleDeleteMemo = async (memoId) => {
    if (!window.confirm('이 메모를 삭제 처리하시겠습니까?\n(내용은 취소선으로 남습니다)')) return;

    try {
      await api.delete(`evaluation/memos/${memoId}/`);
      await fetchMemos();
    } catch (e) {
      console.error(e);
      alert('메모 삭제에 실패했습니다.');
    }
  };

  const handleHardDeleteMemo = async (memoId) => {
    if (!window.confirm('이 메모를 완전삭제하시겠습니까?\n삭제 후에는 복구할 수 없습니다.')) return;
    try {
      await api.delete(`evaluation/memos/${memoId}/hard-delete/`);
      await fetchMemos();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail ?? '완전삭제에 실패했습니다. (권한 확인)');
    }
  };





  // 편람의 모든 지표를 평탄화 (부문 > 지표 > 세부지표)
  const flatIndicators = [];
  manual?.categories?.forEach((cat) => {
    cat.indicators.forEach((ind) => {
      flatIndicators.push({ ...ind, catName: cat.name, areaDisplay: cat.area_display, depth: 0 });
      ind.sub_indicators?.forEach((sub) => {
        flatIndicators.push({ ...sub, catName: cat.name, areaDisplay: cat.area_display, depth: 1 });
      });
    });
  });

  if (error) return <div style={{ padding: 24, color: '#c00' }}>{error}</div>;
  if (!manual || !blobUrl) return <div style={{ padding: 24 }}>PDF 불러오는 중…</div>;

  const pageMemos = memos[page] ?? [];
  // 인쇄된 편람 쪽번호로 환산 (실제 PDF 페이지 - 보정값)
  const printedPage = page - (manual.page_offset ?? 0);

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)' }}>
      {/* 좌측: 지표 목차 */}
      <aside style={{ width: 300, overflowY: 'auto', borderRight: '1px solid #ddd', padding: 12 }}>
        <h3 style={{ marginTop: 0 }}>{manual.year}년 편람</h3>
        <button
          onClick={() => setPage(1)}
          style={{ display: 'inline-block', width: 'auto', padding: '4px 10px', fontSize: 12, marginBottom: 8 }}
        >
          처음으로
        </button>
        {flatIndicators.length === 0 && (
          <p style={{ color: '#888', fontSize: 13 }}>
            등록된 지표가 없습니다. (seed 커맨드 실행 또는 admin에서 입력)
          </p>
        )}

        {flatIndicators.map((ind) => (
          <div
            key={ind.id}
            onClick={() => {
              console.log('클릭됨:', ind.name, 'pdf_page=', ind.pdf_page);
              if (ind.pdf_page) setPage(ind.pdf_page);
            }}

            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 8px',
              paddingLeft: ind.depth ? 24 : 8,
              cursor: ind.pdf_page ? 'pointer' : 'default',
              borderRadius: 4,
              fontSize: 13,
              background: ind.pdf_page === page ? '#eef4ff' : 'transparent',
            }}
          >
            <span style={{ fontWeight: ind.depth ? 400 : 600, pointerEvents: 'none' }}>
              {ind.name}

              {memos[ind.pdf_page]?.length > 0 && (
                <span title="메모 있음" style={{ color: '#e08a00', marginLeft: 4 }}>●</span>
              )}


            </span>
            {ind.page_number && (
              <span style={{ color: '#888', pointerEvents: 'none', flexShrink: 0, marginLeft: 8 }}>
                p.{ind.page_number}
              </span>
            )}
          </div>
        ))}
      </aside>

      {/* 중앙: PDF 뷰어 */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <iframe
          key={page}
          title={`manual-${manual.year}`}
          src={`${blobUrl}#page=${page}`}
          style={{ width: '100%', flex: 1, border: 'none' }}
        />
      </main>


      {/* 우측: 현재 페이지 메모 패널 */}
      <aside style={{ width: 280, borderLeft: '1px solid #ddd', padding: 12, display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ marginTop: 0, marginBottom: 8 }}>
          메모 <span style={{ color: '#888', fontWeight: 400 }}>(p.{printedPage})</span>
        </h4>

        {/* 입력창 + 등록 버튼 */}
        <textarea
          value={memoDraft}
          onChange={(e) => setMemoDraft(e.target.value)}
          placeholder="이 페이지에 대한 메모를 남겨보세요."
          style={{
            height: 80, resize: 'none', padding: 8, fontSize: 13,
            border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box',
          }}
        />
        <button
          onClick={handleAddMemo}
          disabled={memoSaving || !memoDraft.trim()}
          style={{
            display: 'inline-block', width: '100%', marginTop: 8, marginBottom: 12,
            padding: '8px 0', fontSize: 13,
          }}
        >
          {memoSaving ? '등록 중…' : '+ 메모 등록'}
        </button>

        {/* 등록된 메모 카드 목록 */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {pageMemos.length === 0 && (
            <p style={{ color: '#aaa', fontSize: 12 }}>등록된 메모가 없습니다.</p>
          )}


          {pageMemos.map((m) => (
            <div
              key={m.id}
              style={{
                border: '1px solid #eee', borderRadius: 6, padding: '8px 10px',
                background: m.is_deleted ? '#f5f5f5' : '#fafafa', fontSize: 13,
                opacity: m.is_deleted ? 0.6 : 1,
              }}
            >
              <div style={{
                whiteSpace: 'pre-wrap', marginBottom: 6,
                textDecoration: m.is_deleted ? 'line-through' : 'none',
                color: m.is_deleted ? '#999' : 'inherit',
              }}>
                {m.content}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#999' }}>
                  {m.created_by_name || '익명'} · {new Date(m.created_at).toLocaleString()}
                </span>

                {m.is_deleted ? (
                  <span style={{ fontSize: 11, color: '#bbb', whiteSpace: 'nowrap' }}>
                    삭제됨
                    <button
                      onClick={() => handleHardDeleteMemo(m.id)}
                      title="관리자만 가능. 복구 불가."
                      style={{
                        display: 'inline-block', width: 'auto', padding: '2px 8px',
                        fontSize: 11, color: '#c00', marginLeft: 6,
                      }}
                    >
                      완전삭제
                    </button>
                  </span>
                ) : (




                  <button
                    onClick={() => handleDeleteMemo(m.id)}
                    style={{
                      display: 'inline-block', width: 'auto', padding: '2px 8px',
                      fontSize: 11, color: '#c00',
                    }}
                  >
                    삭제
                  </button>
                )}
              </div>
            </div>
          ))}




        </div>
      </aside>


    </div>
  );
}